"use client"

import { useState, type ReactNode } from "react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ArrowDownRight, ArrowUpRight, Clock, LogIn, LogOut } from "lucide-react"
import type { RouteTimelinePoint } from "@/lib/route-timeline"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

// Trilho de rota compartilhado entre o card de resultado da busca e o
// cabeçalho da página de carona. O markup do caso compacto é o da busca,
// copiado palavra por palavra — os dois pontos são a mesma coisa, e existir
// em dois lugares foi o que deixou o cabeçalho com "1h 30m" colado por cima
// de um cálculo comentado.
//
// Com `clockStyle` (página da carona) o trilho vira a timeline vertical do
// /trip-summary: relógio cinza, linha fina e horário/cidade em duas linhas —
// inclusive no caso de dois pontos, que deixa de usar a faixa horizontal.
//
// Variante `full` (rota completa com cidades de passagem): vira trilho
// vertical em todos os breakpoints, porque a faixa horizontal da busca é fixa
// em `w-56` e não cabe N pontos. As paradas fora do trecho do passageiro
// (antes do embarque/depois do desembarque) ficam atrás de um "…": abrir ou
// fechar é estado local, e a duração do topo permanece a do trecho.
//
// O componente não recebe coordenada alguma — só pontos já resolvidos. Isso é
// proposital: a busca devolve `stops` em GeoJSON cru e um componente que não
// sabe o que é latitude não pode quebrar por causa disso.

interface RouteTimelineProps {
  points: RouteTimelinePoint[]
  /** Duração mostrada no topo do trilho. */
  durationLabel: string
  variant: "compact" | "full"
  /**
   * Estilo do /trip-summary (e do "Resumo da Carona"): relógio cinza + linha
   * vertical fina + horário/cidade/nome em duas linhas, em vez do trilho de
   * pontos. Usado pela página da carona; a busca continua no trilho antigo.
   */
  clockStyle?: boolean
  /** Bloco de preço dentro do trilho mobile. */
  mobilePrice?: ReactNode
  /** Bloco de preço no desktop, irmão do trilho. */
  desktopPrice?: ReactNode
}

const DOT = "w-1.5 h-1.5 rounded-full bg-gray-400 z-10 flex-shrink-0"
const TIME = "text-xs font-bold leading-none"
const NAME = "text-sm font-semibold"

function PassengerChip({
  passenger,
  kind,
}: {
  passenger: { name: string; avatar: string | null }
  kind: "board" | "alight"
}) {
  const name = passenger.name.split(" ")[0] || "P"
  return (
    // <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3">
    //   <Avatar className="h-5 w-5">
    //     <AvatarImage src={passenger.avatar || undefined} alt={name} />
    //     <AvatarFallback className="text-[9px]">{name[0] || "P"}</AvatarFallback>
    //   </Avatar>
    //   <span className="text-xs text-gray-700">{name}</span>
    //   <span className={`text-[9px] font-medium ${kind === "board" ? "text-green-600" : "text-amber-600"}`}>
    //     {kind === "board" ? "embarca" : "desce"}
    //   </span>
    // </div>
    <Avatar className="h-8 w-8 border-2 border-white">
      <AvatarImage src={passenger.avatar || undefined} alt={name} />
      <AvatarFallback className="text-[10px]">{name[0] || "P"}</AvatarFallback>
    </Avatar>
  )
}

type Row =
  | { type: "point"; point: RouteTimelinePoint; dim: boolean }
  | { type: "toggle"; side: "before" | "after" | "middle"; count: number; open: boolean }

export function RouteTimeline({
  points,
  durationLabel,
  variant,
  clockStyle = false,
  mobilePrice,
  desktopPrice,
}: RouteTimelineProps) {
  const [showBefore, setShowBefore] = useState(false)
  const [showMiddle, setShowMiddle] = useState(false)
  const [showAfter, setShowAfter] = useState(false)

  // Nome em até duas linhas: cidade/POI em cima e região abaixo. O secundário
  // só aparece quando o ponto já traz `secondary` (a busca mantém uma linha).
  const renderName = (point: RouteTimelinePoint, className: string) => (
    <div className={className}>
      <span className="block truncate">{point.name}</span>
      {point.secondary ? (
        <span className="block truncate text-xs font-normal opacity-60">{point.secondary}</span>
      ) : null}
    </div>
  )

  const compact = variant === "compact" && points.length === 2 && !clockStyle
  const [first, last] = compact ? [points[0], points[1]] : [undefined, undefined]

  if (compact && first && last) {
    return (
      <div className="flex flex-col md:flex-row md:items-center justify-start md:justify-between w-full gap-4">
        {/* LAYOUT MOBILE (Linha do Tempo Vertical) - Visível apenas em telas menores */}
        <div className="flex md:hidden items-stretch gap-4">
          {/* Coluna da Linha e dos Círculos */}
          <div className="flex flex-col justify-between mt-4 mb-6 relative">
            <div className={DOT} />

            {/* Linha Conectora Vertical */}
            <div className="absolute top-1 bottom-1 w-[2px] bg-gray-300 left-[3px] -translate-x-1/2" />

            {/* Texto de Duração centralizado ao lado da linha */}
            <span className="text-xs font-medium text-gray-400 bg-white py-0 my-3 z-10 whitespace-nowrap rotate-0 text-center">
              <small>{durationLabel}</small>
            </span>

            <div className={DOT} />
          </div>

          {/* Coluna dos Textos (Horários e Cidades) */}
          <div className="flex flex-col justify-between py-0.5 space-y-6 ml-[-40px]">
            <div>
              <div className={`${TIME} text-gray-900`}>{first.time}</div>
              {renderName(first, `${NAME} text-gray-700`)}
            </div>
            <div>
              <div className={`${TIME} text-gray-900`}>{last.time}</div>
              {renderName(last, `${NAME} text-gray-700`)}
            </div>
          </div>
          {mobilePrice}
        </div>

        {/* LAYOUT DESKTOP - Oculto no mobile, visível a partir de telas médias (md) */}
        <div className="hidden md:flex items-center justify-between text-sm font-bold text-gray-900 gap-x-3 mr-4">
          {/* Coluna da Esquerda: Origem + Linhas + Duração + Bolinhas */}
          <div className="flex-1">
            <div className="flex items-center gap-x-3 w-full">
              <span className="text-xs font-bold whitespace-nowrap">{first.time}</span>

              {/* Container das Linhas e Duração com Bolinhas */}
              <div className="w-56 flex items-center">
                <div className={`${DOT} mt-1`} />
                <div className="flex-1 h-[2px] bg-gray-300 mt-1" />
                <p className="font-medium text-xs text-gray-400 bg-white px-0 mx-1 z-10 whitespace-nowrap rotate-0 text-center">
                  <small>{durationLabel}</small>
                </p>
                <div className="flex-1 h-[2px] bg-gray-300 mt-1" />
                <div className={`${DOT} mt-1`} />
              </div>
            </div>
            {renderName(first, `${NAME} text-gray-900`)}
          </div>

          {/* Coluna da Direita: Destino */}
          <div className="text-left">
            <div className="text-xs font-bold">{last.time}</div>
            {renderName(last, `${NAME} text-gray-900`)}
          </div>
        </div>
        {desktopPrice}
      </div>
    )
  }

  const before = points.filter((p) => p.group === "before")
  const inside = points.filter((p) => p.group === "in")
  const middle = points.filter((p) => p.group === "middle")
  const after = points.filter((p) => p.group === "after")

  // A duração do topo é sempre a do trecho: abrir os grupos não troca o rótulo
  // pela duração da rota inteira.
  const label = durationLabel

  const rows: Row[] = []
  if (before.length > 0) {
    if (showBefore) for (const point of before) rows.push({ type: "point", point, dim: true })
    rows.push({ type: "toggle", side: "before", count: before.length, open: showBefore })
  }
  inside.forEach((point, i) => {
    rows.push({ type: "point", point, dim: false })
    // O grupo do meio entra logo após o primeiro ponto dentro do trecho —
    // quando ele existe, "dentro" tem exatamente origem e destino.
    if (middle.length > 0 && i === 0) {
      if (showMiddle) for (const p of middle) rows.push({ type: "point", point: p, dim: true })
      else rows.push({ type: "toggle", side: "middle", count: middle.length, open: showMiddle })
    }
  })
  if (after.length > 0) {
    rows.push({ type: "toggle", side: "after", count: after.length, open: showAfter })
    if (showAfter) for (const point of after) rows.push({ type: "point", point, dim: true })
  }

  const toggle = (row: Extract<Row, { type: "toggle" }>) => (
    <button
      type="button"
      onClick={() =>
        row.side === "before"
          ? setShowBefore((v) => !v)
          : row.side === "middle"
            ? setShowMiddle((v) => !v)
            : setShowAfter((v) => !v)
      }
      aria-expanded={row.open}
      className="text-xs font-medium text-gray-400 hover:text-blue-600 whitespace-nowrap"
    >
      {row.open ? "recolher" : `… ver ${row.count} ${row.count === 1 ? "parada" : "paradas"}`}
    </button>
  )

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-start md:justify-between w-full gap-4">
      <div className="flex items-stretch gap-4 w-full">
        <div className="flex-1 min-w-0">
          <div className={`flex ${clockStyle ? "gap-4" : "gap-3"}`}>
            {/* <div className={`shrink-0 ${clockStyle ? "w-5" : "w-1.5"}`} /> */}
            <span className="text-xs font-medium text-gray-400 bg-white whitespace-nowrap text-center">
              <small>{label}</small>
            </span>
          </div>

          <div className="flex flex-col">
{rows.map((row, i) => {
              const isLast = i === rows.length - 1
              const key = row.type === "point" ? row.point.index : `toggle-${row.side}`
              return (
                <div key={key} className={`relative items-stretch ${clockStyle ? "flex gap-4 pt-0.5" : "flex gap-3"}`}>
                  <div className={`flex flex-col items-center shrink-0 ${clockStyle ? "pt-0.5" : "w-1.5"}`}>
                    {row.type === "toggle" ? (
                      clockStyle ? <div className="h-5 w-5" /> : null
                    ) : clockStyle ? (
                      <Clock className="h-5 w-5 text-gray-300 mb-1" />
                    ) : (
                      <div className={DOT} />
                    )}
                    {!isLast && (
                      <div
                        className={clockStyle ? "w-0.5 flex-1 bg-gray-200" : "w-[2px] flex-1 bg-gray-300"}
                      />
                    )}
                  </div>
                  <div className={`min-w-0 flex-1 ${isLast ? "" : "pb-2"}${row.type === "toggle" ? " absolute left-1" : ""}`}>
                    {row.type === "toggle" ? (
                      toggle(row)
                    ) : (
                      <>
                        {clockStyle ? (
                          <>
                            <p className="font-medium text-gray-400">{row.point.time}</p>
                            <p className={`font-medium ${row.dim ? "text-gray-400" : ""}`}>
                              {row.point.name}
                            </p>
                            {row.point.secondary ? (
                              <p className={`text-sm ${row.dim ? "text-gray-400" : "text-gray-500"}`}>
                                {row.point.secondary}
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div
                              className={`${TIME} ${row.dim ? "text-gray-400" : "text-gray-900"}`}
                            >
                              {row.point.time}
                            </div>
                            {renderName(
                              row.point,
                              `${NAME} ${row.dim ? "text-gray-400" : "text-gray-700"}`,
                            )}
                          </>
                        )}
                        <div className="flex items-center">
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                              {(row.point.alighters && row.point.alighters.length > 0) && (
                                <>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <ArrowDownRight className="h-3.5 w-3.5 text-amber-600 mr-1" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-sm">Desembarque</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <div className="flex items-center -space-x-4 mr-2">
                                    {row.point.alighters.map((p) => (
                                      <PassengerChip key={p.name || `alight-${i}`} passenger={p} kind="alight" count={p.count} />
                                    ))}
                                  </div>
                                </>
                              )}
                              {(row.point.boarders && row.point.boarders.length > 0) && (
                                <>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <ArrowUpRight className="h-3.5 w-3.5 text-green-600 mr-1" title="Embarque" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-sm">Embarque</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <div className="flex items-center -space-x-4">
                                    {row.point.boarders.map((p) => (
                                      <PassengerChip key={p.name || `alight-${i}`} passenger={p} kind="board" count={p.count} />
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        {mobilePrice && <div className="flex-1 flex justify-end items-start">{mobilePrice}</div>}
      </div>
      {desktopPrice}
    </div>
  )
}
